import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  Inject,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationRegions } from '@trustedshops/tswp-carrier-b2b-contracts';
import { RxJsSubjectBridge } from '@trustedshops/tswp-core-common-eventing-rxjs';
import {
  AccountWorld,
  ProductFeatureService,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import {
  BreadcrumbsCompositionService,
  NavigationItem,
  NavigationService,
  TOKENS,
  UserInteractionHandler,
} from '@trustedshops/tswp-core-ui';
import { combineLatest, forkJoin, from, Observable, of } from 'rxjs';
import { UserOnboardingInitService } from '../../services/user-onboarding-init.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { catchError, map, shareReplay } from 'rxjs/operators';

@Component({
  selector: 'header-bar',
  templateUrl: './header-bar.component.html',
  styleUrls: ['./header-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
})
export class HeaderBarComponent implements OnInit {
  private static readonly FEAT_INVOICE_MANAGEMENT = 'FEAT_INVOICE_MANAGEMENT';
  private static readonly FEAT_UPGRADE_CENTER = 'FEAT_UPGRADE_CENTRE';
  private static readonly FEAT_ONBOARDING_FREE = 'FEAT_ONBOARDING_FREE';
  private static readonly FEAT_ONBOARDING_PAID = 'FEAT_ONBOARDING_PAID';

  private readonly _navigationService: NavigationService;
  private readonly _breadcrumbsCompositionService: BreadcrumbsCompositionService;
  private _userSettingsTooltipVisible = true;

  /**
   * Gets or sets a value indicating wether the tooltip is visible.
   */
  @Input()
  public get userSettingsTooltipVisible(): boolean {
    return this._userSettingsTooltipVisible;
  }

  public set userSettingsTooltipVisible(v: boolean) {
    this._userSettingsTooltipVisible = coerceBooleanProperty(v);
  }

  private _userNotificationTooltipVisible = true;
  /**
   * Gets or sets a value indicating wether the tooltip is visible.
   */
  @Input()
  public get userNotificationTooltipVisible(): boolean {
    return this._userNotificationTooltipVisible;
  }

  public set userNotificationTooltipVisible(v: boolean) {
    this._userNotificationTooltipVisible = coerceBooleanProperty(v);
  }

  private _breadcrumbs: Observable<NavigationItem[]>;
  public get breadcrumbs(): Observable<NavigationItem[]> {
    return this._breadcrumbs;
  }

  private _metaMenuItems: NavigationItem[];
  public get metaMenuItems(): NavigationItem[] {
    return this._metaMenuItems;
  }

  public set metaMenuItems(v: NavigationItem[]) {
    this._metaMenuItems = v;
  }

  public progress$: Observable<number>;
  public readonly hasFreeOnboarding$: Observable<boolean>;
  public readonly hasInvoiceManagement$: Observable<boolean>;
  public readonly hasUpgradeCenter$: Observable<boolean>;
  public readonly isLegacyAccount$: Observable<boolean>;
  public readonly showUpgradeCenter$: Observable<boolean>;

  public constructor(
    @Inject(TOKENS.NavigationService) navigationService: NavigationService,
    @Inject(TOKENS.BreadcrumbsCompositionService)
    breadcrumbsCompositionService: BreadcrumbsCompositionService,
    @Inject(MASTERDATA_TOKENS.ProductFeatureService)
    private readonly productFeatureService: ProductFeatureService,
    public readonly userOnboardingInitService: UserOnboardingInitService
  ) {
    this._navigationService = navigationService;
    this._breadcrumbsCompositionService = breadcrumbsCompositionService;

    this.hasInvoiceManagement$ = from(
      this.productFeatureService.hasFeature({
        featureNames: HeaderBarComponent.FEAT_INVOICE_MANAGEMENT,
      })
    ).pipe(
      catchError(() => of(false)),
      shareReplay(1)
    );

    this.hasUpgradeCenter$ = from(
      this.productFeatureService.hasFeature({
        featureNames: HeaderBarComponent.FEAT_UPGRADE_CENTER,
      })
    ).pipe(
      catchError(() => of(false)),
      shareReplay(1)
    );

    // UPGRADE-CENTER CTA relevant ONLY for LEGACY ACCOUNTS, NEW_WORLD accounts use per package upgrades
    this.isLegacyAccount$ = from(this.productFeatureService.getFeatures()).pipe(
      map((data) => data?.accountWorld === AccountWorld.Legacy),
      catchError(() => of(false)),
      shareReplay(1)
    );

    this.showUpgradeCenter$ = combineLatest([
      this.hasInvoiceManagement$,
      this.hasUpgradeCenter$,
      this.isLegacyAccount$,
    ]).pipe(
      map(([invoice, upgrade, legacy]) => invoice && upgrade && legacy),
      shareReplay(1)
    );

    // TEMP WORKAROUND (CC-1147 / PSUP-5378 / CC-1236): paid accounts receive both FEAT_ONBOARDING_FREE
    // (via PROD_BASIC_ACCESS) and FEAT_ONBOARDING_PAID (via PROD_PLUS_ACCESS), so the free-tier
    // upgrade button renders alongside the paid upgrade-center CTA (duplicate button). Until the
    // backend ships a clean fix (so FEAT_ONBOARDING_FREE is no longer present on paid accounts),
    // FEAT_ONBOARDING_PAID takes priority here: only show the free button when the account has the
    // free feature but NOT the paid one. Revert to a plain FEAT_ONBOARDING_FREE check once fixed.
    this.hasFreeOnboarding$ = forkJoin({
      free: this.productFeatureService.hasFeature({
        featureNames: HeaderBarComponent.FEAT_ONBOARDING_FREE,
      }),
      paid: this.productFeatureService.hasFeature({
        featureNames: HeaderBarComponent.FEAT_ONBOARDING_PAID,
      }),
    }).pipe(
      map(({ free, paid }) => free && !paid),
      catchError(() => of(false)),
      shareReplay(1)
    );
  }

  public async ngOnInit(): Promise<void> {
    this._metaMenuItems = this._navigationService.getItemsForRegion(
      NavigationRegions.MetaMenu
    );
    this._breadcrumbs =
      this._breadcrumbsCompositionService.currentBreadcrumbTrail.convertWith(
        RxJsSubjectBridge<NavigationItem<any, UserInteractionHandler>[]>
      );
    this.progress$ = this.userOnboardingInitService.progress$;
  }

  public toggleUsersettingsTooltips(): void {
    this.userSettingsTooltipVisible = false;
    this.userNotificationTooltipVisible = true;
  }

  public toggleNotificationTooltip(): void {
    this.userNotificationTooltipVisible = false;
    this.userSettingsTooltipVisible = true;
  }
}
